// React
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Components
import EntityCard from './EntityCard';

// Utils
import { formatDate } from '../../utils/helpers';

// Styles
import '../../css/EntityFrame.css';

const EntitiesList = ({ 
  onSelectEntity, 
  selectedEntityId, 
  entities, 
  entitiesLoading, 
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  reportedEntityIds = [],
}) => {
  const sentinelRef = useRef(null);
  const listRef = useRef(null);

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / pageSize);

  // Infinite scroll: trigger next page load when sentinel enters the scrollable list container
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const list = listRef.current;
    if (!sentinel || !list) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !entitiesLoading && currentPage < totalPages) {
          onPageChange(currentPage + 1);
        }
      },
      { root: list, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [entitiesLoading, currentPage, totalPages, onPageChange]);

  if (!entities) return null;

  const displayText =
    totalCount > 0 ? `Showing ${totalCount} Posts` : `No Posts`;

  const showInitialLoading = entitiesLoading && entities.length === 0;
  const showRefetchOverlay = entitiesLoading && entities.length > 0 && currentPage === 1;

  return (
    <div className="entities-container">
      <div className="entities-list-header">
        <div className="entities-count">
          {entitiesLoading && entities.length > 0 ? (
            <div className="entities-inline-spinner">
              <div className="spinner-small"></div>
            </div>
          ) : (
            <span className="entities-count-number">{displayText}</span>
          )}
          {onRefresh && (
            <button 
              className="entities-refresh-btn"
              onClick={onRefresh}
              disabled={entitiesLoading}
              title="Refresh entities list"
            >
              🔄
            </button>
          )}
        </div>
        
        {totalCount > 0 && (
          <div className="entities-page-size-control">
            <label>
              <select 
                value={pageSize} 
                onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
                disabled={entitiesLoading}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </label>
          </div>
        )}
      </div>

      <div className="entities-list" ref={listRef}>
        {showInitialLoading ? (
          <div className="entities-loading-container">
            <div className="entities-loading-spinner">
              <div className="spinner" />
            </div>
            <img
              src={`${process.env.PUBLIC_URL || ''}/images/cyberwell-logo.png`}
              alt="CyberWell"
              className="app-loading-brand-logo"
            />
            <p className="app-loading-status-text">Loading entities...</p>
          </div>
        ) : (
          <>
            {showRefetchOverlay && (
              <div className="entities-refetch-overlay">
                <div className="entities-loading-spinner">
                  <div className="spinner"></div>
                </div>
              </div>
            )}
            {entities.map(entity => (
              <EntityCard
                key={entity.EntityId}
                entity={entity}
                isSelected={selectedEntityId && String(selectedEntityId) === String(entity.EntityId)}
                compactMode={!selectedEntityId}
                onSelect={onSelectEntity}
                formatDate={formatDate}
                isReported={reportedEntityIds.includes(String(entity.EntityId))}
              />
            ))}
            <div ref={sentinelRef} className="entities-scroll-sentinel" />
            {entitiesLoading && entities.length > 0 && currentPage < totalPages && (
              <div className="entities-load-more-spinner">
                <div className="spinner-small"></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// PropTypes for better type checking and documentation
EntitiesList.propTypes = {
  onSelectEntity: PropTypes.func.isRequired,
  selectedEntityId: PropTypes.string,
  entities: PropTypes.arrayOf(PropTypes.object),
  entitiesLoading: PropTypes.bool,
  totalCount: PropTypes.number,
  currentPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  reportedEntityIds: PropTypes.arrayOf(PropTypes.string),
};

EntitiesList.defaultProps = {
  selectedEntityId: null,
  entities: [],
  entitiesLoading: false,
  totalCount: 0,
  reportedEntityIds: [],
};

export default EntitiesList;
